import { supabase } from '../lib/supabase';

export interface ContestPermission {
  user_id: string;
  contest_id: string;
  permissions: string[];
}

export interface UserPermissions {
  features: string[];
  contests: string[];
  contest_permissions: Record<string, string[]>;
}

/**
 * Synchronise les permissions d'un utilisateur entre contest_permissions et users
 * @param userId ID de l'utilisateur
 * @returns Les permissions mises à jour
 */
export const syncUserPermissions = async (userId: string): Promise<UserPermissions | null> => {
  try {
    // Récupérer toutes les permissions de concours de l'utilisateur
    const { data: contestPermissions, error: fetchError } = await supabase
      .from('contest_permissions')
      .select('*')
      .eq('user_id', userId);

    if (fetchError) throw fetchError;

    // Transformer les données en format UserPermissions
    const permissions: UserPermissions = {
      features: Array.from(new Set(contestPermissions?.flatMap(cp => cp.permissions) || [])),
      contests: contestPermissions?.map(cp => cp.contest_id) || [],
      contest_permissions: contestPermissions?.reduce((acc, cp) => {
        acc[cp.contest_id] = cp.permissions;
        return acc;
      }, {} as Record<string, string[]>) || {}
    };

    // Mettre à jour les permissions dans la table users
    const { error: updateError } = await supabase
      .from('users')
      .update({ permissions })
      .eq('id', userId);

    if (updateError) throw updateError;

    return permissions;
  } catch (error) {
    console.error('Erreur lors de la synchronisation des permissions:', error);
    return null;
  }
};

/**
 * Met à jour les permissions d'un concours pour un utilisateur
 * @param userId ID de l'utilisateur
 * @param contestId ID du concours
 * @param permissions Liste des permissions
 */
export const updateContestPermissions = async (
  userId: string,
  contestId: string,
  permissions: string[]
): Promise<boolean> => {
  try {
    // Mettre à jour ou insérer les permissions du concours
    const { error: upsertError } = await supabase
      .from('contest_permissions')
      .upsert({
        user_id: userId,
        contest_id: contestId,
        permissions
      }, {
        onConflict: 'user_id,contest_id'
      });

    if (upsertError) throw upsertError;

    // Synchroniser les permissions
    await syncUserPermissions(userId);

    return true;
  } catch (error) {
    console.error('Erreur lors de la mise à jour des permissions:', error);
    return false;
  }
};

/**
 * Supprime les permissions d'un concours pour un utilisateur
 * @param userId ID de l'utilisateur
 * @param contestId ID du concours
 */
export const removeContestPermissions = async (
  userId: string,
  contestId: string
): Promise<boolean> => {
  try {
    // Supprimer les permissions du concours
    const { error: deleteError } = await supabase
      .from('contest_permissions')
      .delete()
      .eq('user_id', userId)
      .eq('contest_id', contestId);

    if (deleteError) throw deleteError;

    // Synchroniser les permissions
    await syncUserPermissions(userId);

    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression des permissions:', error);
    return false;
  }
};
